// Civil-date calendar. Never parse YYYY-MM-DD as UTC for display and never
// guess a harvest date from an unknown species or a virtual crop's age.
export const pad = n => String(n).padStart(2, '0');
export function dateKey(date = new Date()) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
export function civil(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
  const [y, m, d] = value.split('-').map(Number);
  const result = new Date(y, m - 1, d, 12);
  return y >= 1900 && y <= 2199 && dateKey(result) === value ? result : null;
}
export function plusDays(key, amount) {
  const d = civil(key);
  if (!d) return null;
  d.setDate(d.getDate() + amount);
  return dateKey(d);
}
const zoneFormatters = new Map();
function zoneFormatter(zone) {
  if (!zoneFormatters.has(zone)) {
    const formatter = new Intl.DateTimeFormat('en-GB', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    if (zoneFormatters.size >= 32) zoneFormatters.delete(zoneFormatters.keys().next().value);
    zoneFormatters.set(zone, formatter);
  }
  return zoneFormatters.get(zone);
}
export function validZone(zone) {
  if (typeof zone !== 'string' || !zone || zone.length > 100) return false;
  try { zoneFormatter(zone); return true; } catch { return false; }
}
export function deviceZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}
function partsAt(ms, zone) {
  const parts = zoneFormatter(zone).formatToParts(ms);
  return Object.fromEntries(parts.map(p => [p.type, p.value]));
}
export function zonedToday(zone, now = Date.now()) {
  const p = partsAt(now, validZone(zone) ? zone : deviceZone());
  return `${p.year}-${p.month}-${p.day}`;
}
export function localDateTime(ms, zone) {
  const p = partsAt(ms, zone);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}
// Reject nonexistent spring-forward times instead of silently shifting an alarm.
// Ambiguous autumn times use the earliest matching instant, shown in the editor.
export function zonedInstant(key, time, zone) {
  if (!civil(key) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time || '') || !validZone(zone)) return null;
  const [y, m, d] = key.split('-').map(Number);
  const [h, min] = time.split(':').map(Number);
  const target = Date.UTC(y, m - 1, d, h, min);
  let guess = target;
  for (let i = 0; i < 4; i += 1) {
    const p = partsAt(guess, zone);
    const delta = target - Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute);
    if (!delta) break;
    guess += delta;
  }
  const matches = ms => { const p = partsAt(ms, zone); return `${p.year}-${p.month}-${p.day}` === key && `${p.hour}:${p.minute}` === time; };
  const candidates = [guess - 7200000, guess - 3600000, guess, guess + 3600000, guess + 7200000].filter(matches);
  return candidates.length ? Math.min(...candidates) : null;
}
export const TYPES = ['event', 'birthday', 'anniversary', 'memorial', 'garden', 'eden'];
export const REPEATS = ['none', 'daily', 'weekly', 'monthly', 'yearly'];
export function validateEvent(data) {
  if (!data || typeof data !== 'object') throw new Error('invalid_event');
  if (typeof data.title !== 'string' || !data.title.trim() || data.title.length > 140) throw new Error('title_required');
  if (!civil(data.date) || !civil(data.endDate || data.date) || (data.endDate || data.date) < data.date) throw new Error('invalid_date');
  if (!validZone(data.timezone)) throw new Error('invalid_timezone');
  if (!TYPES.includes(data.type) || !REPEATS.includes(data.repeat)) throw new Error('invalid_type');
  if (data.until && (!civil(data.until) || data.until < data.date)) throw new Error('invalid_date');
  const start = zonedInstant(data.date, data.allDay ? '00:00' : data.time, data.timezone);
  const end = zonedInstant(data.allDay ? plusDays(data.endDate || data.date, 1) : (data.endDate || data.date), data.allDay ? '00:00' : data.endTime, data.timezone);
  if (start === null || end === null || end <= start || end - start > 366 * 86400000) throw new Error('invalid_time');
  if (data.reminderMinutes != null && (!Number.isInteger(data.reminderMinutes) || Math.abs(data.reminderMinutes) > 43200)) throw new Error('invalid_reminder');
  return { title: data.title.trim(), notes: String(data.notes || '').slice(0, 10000), date: data.date,
    endDate: data.endDate || data.date, time: data.allDay ? '00:00' : data.time, endTime: data.allDay ? '00:00' : data.endTime,
    timezone: data.timezone, allDay: !!data.allDay, type: data.type, repeat: data.repeat,
    until: data.until || '', reminderMinutes: data.reminderMinutes ?? null };
}
export function occurrences(rows, from, to) {
  if (!civil(from) || !civil(to) || to < from) return [];
  const results = [];
  for (const row of (rows || []).slice(0, 5000)) {
    let e;
    try { e = validateEvent(row.data); } catch { continue; }
    const first = civil(e.date), last = civil(e.endDate), durationDays = Math.round((last - first) / 86400000);
    // Bounded day-by-day expansion avoids timezone/DST and month overflow bugs.
    let candidate = plusDays(from, -Math.min(366, durationDays));
    if (!civil(candidate)) candidate = from;
    for (let i = 0; i < 1100 && candidate <= to; i += 1, candidate = plusDays(candidate, 1)) {
      if (candidate < e.date || (e.until && candidate > e.until)) continue;
      const d = civil(candidate), days = Math.round((d - first) / 86400000);
      const sameDay = d.getDate() === first.getDate();
      const leapBirthday = e.type === 'birthday' && first.getMonth() === 1 && first.getDate() === 29 && d.getMonth() === 1 && d.getDate() === 28 && new Date(d.getFullYear(), 1, 29).getMonth() !== 1;
      const match = e.repeat === 'none' ? candidate === e.date : e.repeat === 'daily' ? true : e.repeat === 'weekly' ? days % 7 === 0 : e.repeat === 'monthly' ? sameDay : d.getMonth() === first.getMonth() && (sameDay || leapBirthday);
      if (!match) continue;
      const endDate = plusDays(candidate, durationDays);
      const startMs = zonedInstant(candidate, e.time, e.timezone);
      const endMs = zonedInstant(e.allDay ? plusDays(endDate, 1) : endDate, e.endTime, e.timezone);
      if (startMs === null || endMs === null || endMs <= startMs) continue;
      results.push({ ...e, id: row.id, occurrenceId: `${row.id}:${candidate}`, date: candidate, endDate, startMs, endMs,
        reminderMs: e.reminderMinutes == null ? null : startMs - e.reminderMinutes * 60000 });
    }
  }
  return results.sort((a, b) => a.startMs - b.startMs || a.title.localeCompare(b.title));
}
export function monthGrid(key) {
  const d = civil(key); if (!d) return [];
  d.setDate(1);
  const start = plusDays(dateKey(d), -((d.getDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, i) => plusDays(start, i));
}
export function shiftMonth(key, delta) {
  const d = civil(key); d.setDate(1); d.setMonth(d.getMonth() + delta); return dateKey(d);
}
export function dueReminders(rows, now = Date.now()) {
  const from = dateKey(new Date(now - 32 * 86400000)), to = dateKey(new Date(now + 32 * 86400000));
  return occurrences(rows, from, to).filter(e => e.reminderMs != null && e.reminderMs <= now && e.reminderMs >= now - 86400000);
}
